import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Stack, FontIcon } from '@fluentui/react';
import { useNavigate, useSearchParams, useLocation } from 'react-router';
import { useAppContext } from './AppContext';
import SConsciousApiService from '../services/SConsciousApiService';
import { getToken } from 'firebase/messaging';
import { messaging } from '../utils/firebase';
export interface IConversation {
    conversationId: string;
    otherUserId: string;
    otherUserName: string;
    type: 'chat' | 'feedback';
    lastMessage: string;
    lastAt: string;
    readAt?: string;
    pending?: boolean;
    otherUserEmail?: string;
    inviteId?: string;
    isGroup?: boolean;
    splitId?: string;
    receiptId?: string;
    participantNames?: string;
}

function convId(uid1: string, uid2: string) {
    return [uid1, uid2].sort().join('_');
}

function timeAgo(iso: string): string {
    if (!iso) return '';
    try {
        const diff = Date.now() - new Date(iso).getTime();
        const mins = Math.floor(diff / 60000);
        if (mins < 1) return 'now';
        if (mins < 60) return `${mins}m`;
        const hrs = Math.floor(mins / 60);
        if (hrs < 24) return `${hrs}h`;
        return new Date(iso).toLocaleDateString(undefined, { day: 'numeric', month: 'short' });
    } catch { return ''; }
}


export default function Chat({ startWithUser }: { startWithUser?: { user_id: string; user_name: string; user_email: string } | null }) {
    const { appData, setAppData } = useAppContext();
    const userId = appData?.user?.userId || '';
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const location = useLocation();
    const isFeedbackRoute = location.pathname.toLowerCase() === '/feedback';
    const _api = useMemo(() => new SConsciousApiService(), []);

    // When mounted as a standalone /Chat page, read target user from URL params
    const urlUser = useMemo(() => {
        const uid = searchParams.get('userId');
        if (!uid) return null;
        return { user_id: uid, user_name: searchParams.get('userName') || '', user_email: searchParams.get('userEmail') || '' };
    }, [searchParams]);
    const effectiveStartWith = startWithUser ?? urlUser;

    // ── state ─────────────────────────────────────────────
    const [conversations, setConversations] = useState<IConversation[]>([]);
    const [adminIds, setAdminIds] = useState<string[]>([]);
    const [loadingConvs, setLoadingConvs] = useState(false);

    // new-chat search
    const [searchQuery, setSearchQuery] = useState('');
    const [friendsForChat, setFriendsForChat] = useState<{ user_id: string; user_name: string; user_email: string }[]>([]);
    const searchRef = useRef<HTMLInputElement>(null);
    const [inviteStatus, setInviteStatus] = useState<'idle' | 'sending' | 'sent' | 'error'>('idle');

    // ── notification permission: request automatically on first Chat visit ──
    const isPwa = window.matchMedia('(display-mode: standalone)').matches || (window.navigator as any).standalone === true;

    const requestNotifPermission = async () => {
        try {
            const permission = await Notification.requestPermission();
            if (permission === 'granted') {
                const token = await getToken(messaging, { vapidKey: 'BLBKI2Z21HDQEl9iKl_5jygZGcFix5G7YOHbYFt1UozDUATO8tKAyxVzp-9o8B388OIu1Iglmi9B_mY4k2MOFcY' });
                if (token && userId) {
                    localStorage.setItem('signed_in_fcm_token', token);
                    setAppData({ ...appData, user: { ...appData.user, fcmToken: token } });
                    fetch('/api/registerFCMToken', {
                        method: 'POST',
                        headers: { 'Content-Type': 'application/json' },
                        body: JSON.stringify({ userId, fcmToken: token, isPwa }),
                    });
                }
            }
        } catch {}
    };
    useEffect(() => {
        if (typeof Notification === 'undefined') return;
        const existingToken = localStorage.getItem('signed_in_fcm_token');
        // If already registered but now in PWA mode, re-upsert to mark the token as isPwa: true
        if (existingToken && isPwa && userId) {
            fetch('/api/registerFCMToken', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ userId, fcmToken: existingToken, isPwa: true }),
            });
            return;
        }
        if (existingToken) return;
        if (Notification.permission !== 'denied') {
            requestNotifPermission();
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);
    const [inviteError, setInviteError] = useState('');

    // ── load conversations list ────────────────────────────────────────
    const loadConversations = useCallback(async () => {
        if (!userId) return;
        setLoadingConvs(true);
        const [convs, admins] = await Promise.all([
            _api.getChatConversations(userId),
            _api.getChatAdmins(),
        ]);
        setConversations(convs);
        setAdminIds(admins);
        setLoadingConvs(false);
        // Mark all currently-unread conversations as read so the server stays in sync,
        // then zero the badge (source of truth is now the server).
        const unread = convs.filter((c: any) => !c.pending && c.lastAt && (!c.readAt || new Date(c.lastAt) > new Date(c.readAt)));
        if (unread.length > 0) {
            unread.forEach((c: any) => _api.markConversationRead(userId, c.conversationId));
        }
        setAppData({ newMessages: 0 });
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [userId, _api]);

    useEffect(() => { loadConversations(); }, [loadConversations]);

    // Load registered friends for inline search (on mount)
    useEffect(() => {
        if (!userId) return;
        _api.getFriends(userId).then((friends: any[]) => {
            setFriendsForChat(
                friends
                    .filter(f => f.friend_user_id && f.status === 'registered')
                    .map(f => ({ user_id: f.friend_user_id, user_name: f.friend_name || f.friend_email, user_email: f.friend_email }))
            );
        });
    }, [userId, _api]);

    // Always show admin conversations even before any message is sent.
    // If an admin is also a registered friend (e.g. they invited this user), treat them
    // as a normal chat contact — not as "sConscious Support".
    const allConversations = useMemo<IConversation[]>(() => {
        const friendIds = new Set(friendsForChat.map(f => f.user_id));
        // Override type to 'chat' for any conversation whose other user is a friend (regardless of stored type)
        const result: IConversation[] = conversations.map(c =>
            c.type === 'feedback' && friendIds.has(c.otherUserId)
                ? { ...c, type: 'chat' as const }
                : c
        );
        for (const adminId of adminIds) {
            if (adminId === userId) continue;
            // Don't add a support stub for admins who are already friends
            if (friendIds.has(adminId)) continue;
            const cid = convId(userId, adminId);
            if (!result.find(c => c.conversationId === cid)) {
                result.unshift({
                    conversationId: cid,
                    otherUserId: adminId,
                    otherUserName: 'sConscious Support',
                    type: 'feedback',
                    lastMessage: '',
                    lastAt: '',
                });
            }
        }
        return result;
    }, [conversations, adminIds, userId, friendsForChat]);

    // ── open conversation ──────────────────────────────────────────────
    const openConversation = useCallback((conv: IConversation, backTo = '/Chat') => {
        const readNow = new Date().toISOString();
        setConversations(prev => prev.map(c =>
            c.conversationId === conv.conversationId ? { ...c, readAt: readNow } : c
        ));
        _api.markConversationRead(userId, conv.conversationId);
        navigate('/Conversation', { state: { conv, backTo } });
    }, [navigate, _api, userId]);

    const startChatWith = useCallback((u: { user_id: string; user_name: string; user_email: string }) => {
        const conv: IConversation = {
            conversationId: convId(userId, u.user_id),
            otherUserId: u.user_id,
            otherUserName: u.user_name || u.user_email,
            type: 'chat',
            lastMessage: '',
            lastAt: '',
        };
        openConversation(conv);
    }, [userId, openConversation]);

    // Auto-open a conversation when navigated from Friends tab
    useEffect(() => {
        if (effectiveStartWith) startChatWith(effectiveStartWith);
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [effectiveStartWith]);

    // Auto-open admin/support chat when arriving via /Feedback
    useEffect(() => {
        if (!isFeedbackRoute || !userId || adminIds.length === 0) return;
        const adminId = adminIds.find(id => id !== userId);
        if (!adminId) return;
        const conv: IConversation = {
            conversationId: convId(userId, adminId),
            otherUserId: adminId,
            otherUserName: 'sConscious Support',
            type: 'feedback',
            lastMessage: '',
            lastAt: '',
        };
        openConversation(conv, '/');
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isFeedbackRoute, adminIds, userId]);

    // ── inline search helpers ─────────────────────────────────────────
    const handleSearchQuery = (q: string) => {
        setSearchQuery(q);
    };

    const handleInvite = async (email?: string) => {
        const target = (email ?? searchQuery).trim().toLowerCase();
        if (!target || !target.includes('@')) return;
        setInviteStatus('sending');
        setInviteError('');
        const ok = await _api.inviteFriend(userId, target);
        if (ok) {
            setInviteStatus('sent');
            setSearchQuery('');
            setTimeout(() => setInviteStatus('idle'), 3000);
        } else {
            setInviteStatus('error');
            setInviteError('Failed to send invite. Please try again.');
        }
    };

    // ── CONVERSATIONS LIST ─────────────────────────────────────────────
    return (
        <Stack horizontalAlign="center" style={{ width: '100%', margin: '0 auto' }}>
            <div style={{ width: '100%', maxWidth: 640, margin: '0 auto' }}>

                <div className="chat-list__title">Chats</div>

                {/* Inline search + invite bar */}
                <div style={{ padding: '0 14px 8px', display: 'flex', gap: 8, alignItems: 'center' }}>
                    <div style={{ position: 'relative', flex: 1 }}>
                        <FontIcon
                            iconName={searchQuery ? 'ChromeClose' : 'Search'}
                            onClick={searchQuery ? () => handleSearchQuery('') : undefined}
                            style={{
                                position: 'absolute', left: 10, top: '50%', transform: 'translateY(-50%)',
                                fontSize: searchQuery ? 11 : 14, color: 'black',
                                cursor: searchQuery ? 'pointer' : 'default',
                                pointerEvents: searchQuery ? 'all' : 'none',
                                zIndex: 1,
                            }}
                        />
                        <input
                            ref={searchRef}
                            className="darkInput"
                            type="text"
                            placeholder="Search chats or invite by email…"
                            value={searchQuery}
                            onChange={e => handleSearchQuery(e.target.value)}
                            style={{ width: '100%', paddingLeft: '2rem', boxSizing: 'border-box' }}
                        />
                    </div>
                    {/* <button
                        title={searchQuery.trim().includes('@') && !friendsForChat.some(f => f.user_email.toLowerCase() === searchQuery.trim().toLowerCase()) ? 'Invite' : 'Search'}
                        onClick={() => {
                            const isEmail = searchQuery.trim().includes('@');
                            const alreadyFriend = friendsForChat.some(f => f.user_email.toLowerCase() === searchQuery.trim().toLowerCase());
                            if (isEmail && !alreadyFriend) {
                                handleInvite();
                            } else {
                                searchRef.current?.focus();
                            }
                        }}
                        style={{ background: '#000', border: 'none', borderRadius: '50%', width: 36, height: 36, display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', fontFamily: 'inherit', flexShrink: 0, color: '#ffcc00', fontSize: 20 }}
                    >
                        <FontIcon
                            iconName={searchQuery.trim().includes('@') && !friendsForChat.some(f => f.user_email.toLowerCase() === searchQuery.trim().toLowerCase()) ? 'AddFriend' : 'Add'}
                            style={{ color: '#ffcc00' }}
                        />
                    </button> */}
                </div>

                {/* Inline search results: friends + email invite */}
                {searchQuery.trim() && (() => {
                    const lowerQ = searchQuery.trim().toLowerCase();
                    const isEmail = lowerQ.includes('@');
                    const exactEmailMatch = friendsForChat.some(f => f.user_email?.toLowerCase() === lowerQ);
                    const matchingFriends = !isEmail
                        ? friendsForChat.filter(f =>
                            (f.user_name.toLowerCase().includes(lowerQ) || f.user_email.toLowerCase().includes(lowerQ)) &&
                            !allConversations.some(c => c.otherUserId === f.user_id)
                        )
                        : [];
                    if (matchingFriends.length === 0 && (!isEmail || exactEmailMatch)) return null;
                    return (
                        <div style={{ borderBottom: '1px solid rgba(0,0,0,0.1)', marginBottom: 4, paddingBottom: 4 }}>
                            {isEmail && !exactEmailMatch && (
                                <div className="chat-list__item" onClick={() => handleInvite()}>
                                    <div className="chat-list__avatar" style={{ fontSize: 18 }}><FontIcon iconName="Mail" style={{ color: '#ffcc00', fontSize: 18 }} /></div>
                                    <div style={{ flex: 1, minWidth: 0 }}>
                                        <div className="chat-list__name">Invite {searchQuery.trim()}</div>
                                        <div className="chat-list__time" style={{ textAlign: 'left' }}>Send an invitation by email</div>
                                    </div>
                                </div>
                            )}
                            {matchingFriends.length > 0 && (
                                <>
                                    <div style={{ padding: '4px 18px 2px', fontSize: '0.72em', opacity: 0.5, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.5 }}>Friends</div>
                                    {matchingFriends.map(u => (
                                        <div key={u.user_id} className="chat-list__item" onClick={() => { setSearchQuery(''); startChatWith(u); }}>
                                            <div className="chat-list__avatar">{(u.user_name || u.user_email)[0]?.toUpperCase() || '👤'}</div>
                                            <div style={{ flex: 1, minWidth: 0 }}>
                                                <div className="chat-list__name">{u.user_name || u.user_email}</div>
                                            </div>
                                        </div>
                                    ))}
                                </>
                            )}
                        </div>
                    );
                })()}



                {/* Invite feedback */}
                {inviteStatus === 'sent' && (
                    <div style={{ padding: '6px 18px 8px', color: '#000000', fontSize: '0.82em', fontWeight: 600 }}>✓ Invite sent!</div>
                )}
                {inviteStatus === 'error' && (
                    <div style={{ padding: '6px 18px 8px', color: '#dc2626', fontSize: '0.82em' }}>{inviteError}</div>
                )}

                {loadingConvs && <div style={{ padding: '12px 18px', opacity: 0.4, fontSize: '0.85em' }}>Loading…</div>}

                {!loadingConvs && allConversations.length === 0 && (
                    <div style={{ padding: '48px 20px', opacity: 0.35, fontSize: '0.85em', textAlign: 'center' }}>No conversations yet.</div>
                )}

                {(searchQuery.trim() && !searchQuery.trim().includes('@')
                    ? allConversations.filter(c => c.otherUserName.toLowerCase().includes(searchQuery.trim().toLowerCase()))
                    : allConversations
                ).map(conv => {
                    const isFeedback = conv.type === 'feedback';
                    const isPending = !!conv.pending;
                    const isGroup = !!conv.isGroup;
                    const initials = conv.otherUserName.split(' ').map(w => w[0]).join('').toUpperCase().slice(0, 2);
                    const isUnread = !isPending && !!conv.lastAt && (!conv.readAt || new Date(conv.lastAt) > new Date(conv.readAt));

                    const handleClick = () => {
                        if (isGroup && conv.splitId) {
                            const readNow = new Date().toISOString();
                            setConversations(prev => prev.map(c =>
                                c.conversationId === conv.conversationId ? { ...c, readAt: readNow } : c
                            ));
                            _api.markConversationRead(userId, conv.conversationId);
                            navigate(`/BillSplit?splitId=${encodeURIComponent(conv.splitId)}`);
                        } else {
                            openConversation(conv);
                        }
                    };

                    return (
                        <div key={conv.conversationId} className={`chat-list__item${isUnread ? ' chat-list__item--unread' : ''}${isPending ? ' chat-list__item--pending' : ''}`} onClick={handleClick}>
                            <div style={{ position: 'relative', flexShrink: 0 }}>
                                <div className="chat-list__avatar" style={{ background: isGroup ? '#000' : isPending ? 'rgba(120,120,120,0.25)' : isFeedback ? undefined : '#1a1a2e', fontSize: isPending || isFeedback || isGroup ? 18 : 14, color: isGroup ? '#ffcc00' : undefined }}>
                                    {isGroup ? <FontIcon iconName="People" style={{ color: '#ffcc00', fontSize: 18 }} /> : isPending ? <FontIcon iconName="Mail" style={{ color: '#ffcc00', fontSize: 18 }} /> : isFeedback ? '🛡' : initials || '👤'}
                                </div>
                                {isUnread && <span className="chat-list__unread-dot" />}
                            </div>
                            <div style={{ flex: 1, minWidth: 0 }}>
                                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                    <span className="chat-list__name" style={isPending ? { opacity: 0.6 } : undefined}>{conv.otherUserName}</span>
                                    <span className="chat-list__time">{timeAgo(conv.lastAt)}</span>
                                </div>
                                {isPending && <div style={{ fontSize: '0.75em', opacity: 0.45, marginTop: 1 }}>Invited – waiting to join</div>}
                                {isGroup && conv.participantNames && (
                                    <div style={{ fontSize: '0.72em', opacity: 0.45, marginTop: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                        {(() => { try { return JSON.parse(conv.participantNames).join(', '); } catch { return ''; } })()}
                                    </div>
                                )}
                            </div>
                        </div>
                    );
                })}
            </div>
        </Stack>
    );
}

